import React, { useCallback, useState, useEffect, useMemo } from "react";
import styles from "./styles.module.scss";
import Member from "./member";
import Button from "../../../../components/new-button";
import Search from "../../../../components/SearchInput/SearchInput";
import useCommonStore from "../../../../stores/common";
import AddMemberModal from "./add-member-modal";
import Plus from "../../../../assets/svg/icons/common/Plus";
import ResetPasswordModal from "./reset-password-modal";
import RemoveMember from "./delete-modal";
import useOrganizationStore from "../../../../stores/organization";
import { SearchOrganizationMembers, UpdateMember } from "../../../../../api";
import toast from "react-hot-toast";
import { debounce } from "lodash";
import Spinner from "../../../../components/Loaders/Spinner";
import useUserStore from "../../../../stores/user";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";

const Members = ({ fetchData }) => {
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedMemberData, setSelectedMemberData] = useState(null);
  const members = useOrganizationStore(
    (state) => state.selectedOrganizationData?.users,
  );
  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationData?.organization?.id,
  );
  const setSelectedOrganizationData = useOrganizationStore(
    (state) => state.setSelectedOrganizationData,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [removeMemberModalOpen, setRemoveMemberModalOpen] = useState(false);
  const isAdmin = useUserStore((state) => state.isAdmin);

  const isMobile = useMediaQuery(768);

  const debouncedSearchMembers = useMemo(
    () =>
      debounce(async (query) => {
        if (!query || query.length < 1) {
          setSearchResults([]);
          setIsSearching(false);
          return;
        }

        setIsSearching(true);
        try {
          const response = await SearchOrganizationMembers(
            selectedOrganizationId,
            {
              q: query,
            },
          );
          setSearchResults(response.users || []);
        } catch (error) {
          console.error(error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 300),
    [selectedOrganizationId],
  );

  useEffect(() => {
    return () => {
      debouncedSearchMembers.cancel();
    };
  }, [debouncedSearchMembers]);

  const handleSearchTextChange = (e) => {
    setSearchQuery(e.target.value);

    if (e.target.value.length >= 1) {
      setIsSearching(true);
      debouncedSearchMembers(e.target.value);
    } else {
      setIsSearching(false);
      setSearchResults([]);
    }
  };

  const handleAddMember = () => {
    setSelectedMemberData(null);
    setAddMemberModalOpen(true);
  };

  const handleEditMember = (member) => {
    setSelectedMemberData(member);
    setAddMemberModalOpen(true);
  };

  const updateMemberStatus = (member, status) => {
    let tempName = member.name.split(" ");
    let firstName = "";
    let lastName = "";
    if (tempName.length > 1) {
      lastName = tempName[tempName.length - 1];
      tempName.pop();
      firstName = tempName.toString().replace(/,/g, " ");
    } else if (tempName.length > 0) {
      firstName = tempName[0];
    }
    member["disabled"] = status;
    member["first_name"] = firstName;
    member["last_name"] = lastName !== "" ? lastName : "placeholder";
    member["sub_org"] = member?.sub_org_id;

    UpdateMember(selectedOrganizationId, member)
      .then((response) => {
        if (response.success) {
          setSelectedOrganizationData((prev) => ({
            ...prev,
            users: response.users,
          }));
          toast.success("Member status updated successfully", { duration: 5000 });
          if (response?.subAgentAttempted && !response?.subAgentStatus) {
            toast.error(`SubAgent Update API request failed: ${response.subAgentResponseMessage}`, { icon: "🚨", duration: 30000 });
          }
        } else {
          toast.error(response.message, { duration: 5000 });
          console.error(response.message);
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to update member status", { duration: 5000 });
      });
  };

  const displayedMembers = searchQuery.length >= 1 ? searchResults : members;

  return (
    <div
      className={`${styles.members} ${expandDetails ? styles.expanded : ""}`}
    >
      <div className={styles.header}>
        {members && members.length > 0 && (
          <div className={styles.searchWrapper}>
            <Search
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchTextChange}
            />
          </div>
        )}
        {isAdmin && (
          <Button variant="primary" onClick={handleAddMember} style={{ padding: isMobile ? "8px" : "8px 16px" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Plus /> <span className={styles.buttonText}>Add member</span>
            </span>
          </Button>
        )}
      </div>
      <div className={styles.membersList}>
        {isSearching ? (
          <div className={styles.loading}>
            <Spinner />
          </div>
        ) : displayedMembers && displayedMembers.length > 0 ? (
          displayedMembers.map((member, index) => (
            <React.Fragment key={`member-${member.id}`}>
              <Member
                viewType={expandDetails ? "card" : "list"}
                member={member}
                onStatusChange={updateMemberStatus}
                onResetPassword={(memberId) => {
                  setSelectedMember(memberId);
                  setResetPasswordModalOpen(true);
                }}
                onEdit={(member) => {
                  setSelectedMemberData(member);
                  setAddMemberModalOpen(true);
                }}
                onRemove={(memberId) => {
                  setSelectedMember(memberId);
                  setRemoveMemberModalOpen(true);
                }}
              />
              {!expandDetails && index !== displayedMembers.length - 1 && (
                <span className={styles.divider} />
              )}
            </React.Fragment>
          ))
        ) : !isSearching && searchQuery.length === 0 ? (
          <div className={styles.noData}>
            <p>No members to display</p>
          </div>
        ) : null}

        {!isSearching &&
          searchQuery.length >= 1 &&
          searchResults.length === 0 && (
            <div className={styles.noResults}>
              <p>No results found</p>
            </div>
          )}
      </div>

      {addMemberModalOpen && (
        <AddMemberModal
          open={addMemberModalOpen}
          onClose={() => {
            setAddMemberModalOpen(false);
            setSelectedMemberData(null);
            fetchData();
          }}
          isEdit={selectedMemberData !== null}
          selectedMember={selectedMemberData}
        />
      )}

      <RemoveMember
        open={removeMemberModalOpen}
        memberId={selectedMember}
        onClose={() => {
          setRemoveMemberModalOpen(false);
          setSelectedMember(null);
        }}
      />

      <ResetPasswordModal
        open={resetPasswordModalOpen}
        memberId={selectedMember}
        onClose={() => {
          setResetPasswordModalOpen(false);
          setSelectedMember(null);
        }}
      />
    </div>
  );
};

export default Members;
